import React, { useState, useEffect } from "react";
import Sidebar from "./components/Sidebar";
import ChatInterface from "./components/ChatInterface";
import WelcomeScreen from "./components/WelcomeScreen";
import AuthScreen from "./components/AuthScreen";
import { getSessions, getSession, deleteSession } from "./services/api";
import "./App.css";

const App = () => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [sessionMessages, setSessionMessages] = useState([]);
  const [token, setToken] = useState(localStorage.getItem("zanox_token"));
  const [username, setUsername] = useState(localStorage.getItem("zanox_username"));

  useEffect(() => {
    if (token) fetchSessions();
  }, [token]);

  const fetchSessions = async (retryCount = 0) => {
    try {
      const data = await getSessions();
      setSessions(data.sessions || []);
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
      if (retryCount < 3) {
        console.log(`Retrying sessions fetch... (${retryCount + 1}/3)`);
        setTimeout(() => fetchSessions(retryCount + 1), 2000);
        return;
      }
    }
  };

  const handleAuth = (newToken, newUsername) => {
    setToken(newToken);
    setUsername(newUsername);
    localStorage.setItem("zanox_token", newToken);
    localStorage.setItem("zanox_username", newUsername);
  };

  const handleLogout = () => {
    setToken(null);
    setUsername(null);
    setActiveSession(null);
    setShowWelcome(true);
    setSessionMessages([]);
    localStorage.removeItem("zanox_token");
    localStorage.removeItem("zanox_username");
  };

  const handleNewSession = () => {
    setActiveSession(null);
    setShowWelcome(true);
    setSessionMessages([]);
  };

  const handleSelectSession = async (sessionId) => {
    setActiveSession(sessionId);
    setShowWelcome(false);
    try {
      const data = await getSession(sessionId);
      if (data.conversation?.messages) {
        setSessionMessages(data.conversation.messages);
      }
    } catch (err) {
      console.error("Failed to load session:", err);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await deleteSession(sessionId);
      if (activeSession === sessionId) {
        setActiveSession(null);
        setShowWelcome(true);
        setSessionMessages([]);
      }
      fetchSessions();
    } catch (err) {
      console.error("Failed to delete session:", err);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Delete all conversation sessions?")) return;
    for (const s of sessions) {
      await deleteSession(s.sessionId);
    }
    setActiveSession(null);
    setShowWelcome(true);
    setSessionMessages([]);
    fetchSessions();
  };

  const handleFirstMessage = (firstMsg, sessionId) => {
    if (firstMsg) {
      setSessionMessages([{ role: "user", content: firstMsg, id: Date.now() }]);
      if (sessionId) setActiveSession(sessionId);
    }
    setShowWelcome(false);
  };

  if (!token) {
    return (
      <div className="app-container">
        <main className="main-content">
          <header className="top-bar">
            <div className="top-bar-title">
              <i className="fas fa-infinity"></i>
              <span>ZANOX</span>
            </div>
            <div className="top-bar-status">
              <span className="status-dot"></span>
              <span>Awaiting Access</span>
            </div>
          </header>
          <AuthScreen onAuth={handleAuth} />
        </main>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar
        sessions={sessions}
        activeSession={activeSession}
        onNewSession={handleNewSession}
        onSelectSession={handleSelectSession}
        onDeleteSession={handleDeleteSession}
        onClearAll={handleClearAll}
        username={username}
        onLogout={handleLogout}
      />
      <main className="main-content">
        <header className="top-bar">
          <div className="top-bar-title">
            <i className="fas fa-infinity"></i>
            <span>ZANOX</span>
          </div>
          <div className="top-bar-right">
            <span className="top-bar-user">
              <i className="fas fa-user-circle"></i>
              {username}
            </span>
            <button className="logout-btn" onClick={handleLogout} title="Sign out">
              <i className="fas fa-sign-out-alt"></i>
            </button>
            <div className="top-bar-status">
              <span className="status-dot"></span>
              <span>Operational</span>
            </div>
          </div>
        </header>
        <div className="content-area">
          {showWelcome ? (
            <WelcomeScreen onFirstMessage={handleFirstMessage} />
          ) : (
            <ChatInterface
              sessionId={activeSession}
              initialMessages={sessionMessages}
              onSessionCreated={(id) => {
                setActiveSession(id);
                fetchSessions();
              }}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default App;
